import { css } from "@compiled/react";
import { UniformRichTextNode } from "@uniformdev/canvas-react";
import {
    isRichTextValueConsideredEmpty,
    RichTextNode
} from "@uniformdev/richtext";
import { renderToStaticMarkup } from "react-dom/server";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";

import {
    blueShapesSVG,
    eyelashes,
    orangePurpleShapesSVG
} from "./heading-svgs";

export type DesignRefreshEmphasisStyle = "blueShapes" | "orangePurpleShapes";
export type DesignRefreshTextColor = "dark" | "light";

export interface UniformDesignRefreshHeadingProps {
    heading?: RichTextNode | { root: RichTextNode };
    subheading?: RichTextNode | { root: RichTextNode };
    emphasisStyle?: DesignRefreshEmphasisStyle;
    textColor?: DesignRefreshTextColor;
}

const UniformDesignRefreshHeading = ({
    heading,
    subheading,
    emphasisStyle = "blueShapes",
    textColor = "dark"
}: UniformDesignRefreshHeadingProps) => {
    const textColorClass = textColor === "light" ? "textColor-light" : "";

    const getNode = (
        value: RichTextNode | { root: RichTextNode } | undefined
    ): RichTextNode | undefined => {
        if (!value) return undefined;
        return (
            (value as { root: RichTextNode }).root || (value as RichTextNode)
        );
    };

    const headingNode = getNode(heading);
    const subheadingNode = getNode(subheading);

    const hasHeading = heading && !isRichTextValueConsideredEmpty(heading);
    const hasSubheading =
        subheading && !isRichTextValueConsideredEmpty(subheading);

    return (
        <div css={containerCss}>
            {hasHeading && headingNode && (
                <div
                    css={[headingCss]}
                    className={`design-refresh-heading ${emphasisStyle} ${textColorClass}`}
                >
                    <UniformRichTextNode
                        node={headingNode}
                        resolveRichTextRenderer={resolveHeadingRenderer}
                    />
                </div>
            )}
            {hasSubheading && subheadingNode && (
                <div css={subheadingCss} className={textColorClass}>
                    <UniformRichTextNode
                        node={subheadingNode}
                        resolveRichTextRenderer={resolveSubheadingRenderer}
                    />
                </div>
            )}
        </div>
    );
};

const cssifySVG = (svgElement: React.ReactElement) => {
    const svgString = renderToStaticMarkup(svgElement);
    return (
        'url("data:image/svg+xml;charset=utf-8,' +
        encodeURIComponent(svgString) +
        '")'
    );
};

const containerCss = css({
    position: "relative"
});

const headingCss = css({
    "&.design-refresh-heading": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h1, .h1": {
            margin: 0,
            lineHeight: 1.1,
            fontSize: "4.5rem",
            marginBlockEnd: token("space.200"),
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            em: {
                "position": "relative",
                "fontStyle": "normal",
                "&::before": {
                    content: "",
                    position: "absolute",
                    insetBlockStart: "-23px",
                    insetInlineStart: "-52px",
                    height: "108px",
                    width: "56px",
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    pointerEvents: "none",
                    [media.below.md]: {
                        transform: "scaleX(-1) scale(0.75)",
                        insetInlineStart: "auto",
                        insetInlineEnd: "-40px"
                    }
                }
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            strong: {
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                backgroundPosition: "center -3rem",
                backgroundSize: "100% 200%"
            },
            [media.below.md]: {
                fontSize: "2.5rem"
            }
        }
    },
    "&.blueShapes": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "strong": {
            backgroundColor: wacToken("color.extended.blue500"),
            backgroundImage: cssifySVG(blueShapesSVG())
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "em::before": {
            backgroundImage: cssifySVG(
                eyelashes(wacToken("color.extended.blue500"))
            )
        }
    },
    "&.orangePurpleShapes": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "strong": {
            backgroundColor: wacToken("color.extended.purple500"),
            backgroundImage: cssifySVG(orangePurpleShapesSVG())
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "em::before": {
            backgroundImage: cssifySVG(
                eyelashes(wacToken("color.extended.purple500"))
            )
        }
    },
    "&.textColor-light": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h1, .h1": {
            color: wacToken("color.neutral.light0")
        }
    }
});

const subheadingCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p": {
        margin: 0,
        fontSize: "1.25rem",
        lineHeight: 1.5
    },
    "&.textColor-light": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "p, .p": {
            color: wacToken("color.neutral.light0")
        }
    }
});

function resolveHeadingRenderer(node: RichTextNode) {
    if (node.type === "heading" || node.type === "paragraph") {
        return ({ children }: { children?: React.ReactNode }) => (
            <h1>{children}</h1>
        );
    }
    return undefined;
}

function resolveSubheadingRenderer(node: RichTextNode) {
    if (node.type === "paragraph") {
        return ({ children }: { children?: React.ReactNode }) => (
            <p>{children}</p>
        );
    }
    return undefined;
}

export default UniformDesignRefreshHeading;
